<template>
  <div class="ingredient-action-row">
    <template v-if="showEditButtons">
      <a class="button is-primary" :href="editIngredientPath(props.ingredientId)">
        Edit ingredient
      </a>
      <a
        class="button is-danger"
        :href="ingredientPath(props.ingredientId)"
        data-confirm="Are you sure?"
        data-method="delete"
      >
        Destroy
      </a>
    </template>

    <button v-if="userId && totalEntries" class="button is-warning" @click="handleClickReplace">
      {{ replaceButtonText }}
    </button>
  </div>

  <RecipeSelector
    :recipe-search-store="recipeSearchStore"
    :recipe-tag-store="recipeTagStore"
    :show-tags="showTags"
    :list-component="RecipeList"
    :total-entries="recipeSearchStore.totalEntries"
    :user-has-archived-recipes="userHasArchivedRecipes"
    search-placeholder="Filter recipes..."
    show-results-count
    show-filters
    :show-recipe-buttons="false"
    :show-recipe-actions-column="false"
    :load="false"
  />

  <IngredientReplacementSearch
    ref="ingredientReplacementSearch"
    :ingredient-id="ingredientId"
    :ingredient-name="ingredientName"
    :user-id="userId"
    :admin-mode="adminMode"
    :food-mode="foodMode"
    :scope-filter-options="scopeFilterOptions"
    :scopes-allowing-blank-query="scopesAllowingBlankQuery"
  />
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import RecipeSelector from "../recipe-selector.vue";
  import RecipeList from "../recipe-list.vue";
  import IngredientReplacementSearch from "./ingredient-replacement-search.vue";

  import { FOOD, RECIPES_CONTAINING_INGREDIENT_SEARCH_STORE } from "../stores/constants";
  import { errorHelpTip } from "../lib/constants";
  import { useRecipeSearchStore } from "../stores/recipe-search-store";
  import { useRecipeTagStore } from "../stores/recipe-tag-store";

  import { Flash } from "~/jose_modern/flash";
  import { recipeFilterIngredientPath, editIngredientPath, ingredientPath } from "~/routes";

  import { onBeforeMount } from "vue";

  const PER_PAGE = 50;

  const props = defineProps({
    ingredientId: { type: Number, required: true },
    userId: { type: Number, required: false, default: null },
    userHasArchivedRecipes: { type: Boolean, default: true },
    showEditButtons: { type: Boolean, default: false },

    // Following props are just for passing down to ingredient-replacement-search:
    ingredientName: { type: String, required: true },
    adminMode: { type: Boolean, default: false },
    foodMode: { type: String, default: FOOD },
    scopeFilterOptions: { type: Object, required: true },
    scopesAllowingBlankQuery: { type: Array, default: () => [] },
  });

  const showTags = props.userId !== null;

  const recipeSearchStore = useRecipeSearchStore(RECIPES_CONTAINING_INGREDIENT_SEARCH_STORE);
  const recipeTagStore = useRecipeTagStore();

  const ingredientReplacementSearch = _ref(null);

  onBeforeMount(() => {
    recipeSearchStore.setUserId(props.userId);
    recipeSearchStore.setSearchEndpoint(recipeFilterIngredientPath(props.ingredientId));
    recipeSearchStore.assignInitial({ perPage: PER_PAGE }, { patch: true });

    if (props.userId) {
      recipeTagStore.setUserId(props.userId);
      recipeTagStore.loadAllTags();
    }

    recipeSearchStore.resetSearch();
  });

  const totalEntries = _computed(() => recipeSearchStore.totalEntries);
  const replaceButtonText = _computed(() => {
    const n = totalEntries.value || 0;
    return n > 0 ? `Replace ingredient in ${n} recipe${n > 1 ? "s" : ""}` : "Replace ingredient";
  });

  function handleClickReplace() {
    const componentInstance = ingredientReplacementSearch.value;
    try {
      componentInstance.openIngredientSearchModal();
    } catch (err) {
      console.error(`Error calling (${componentInstance}).openIngredientSearchModal();`);
      Flash.error(`Unexpected error. ${errorHelpTip}`);
    }
  }
</script>

<style lang="scss" scoped>
  .ingredient-action-row {
    user-select: none;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
</style>
